import React, { useState, useEffect } from 'react';
import DatePicker from './DatePicker';
import './SalesActivity.css';
import { getLocalDateString, toLocalDateString, formatCurrencyDisplay } from '../utils/formatters';
import LoadingState from './LoadingState';
import ConfirmModal from './ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function InventoryInput({ user, managedOwner }) {
  const [allEntries, setAllEntries] = useState([]);
  const [openROEntries, setOpenROEntries] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [viewMode, setViewMode] = useState('inventory'); // 'inventory' or 'openro'

  const isOwner = user?.role === 'OWNER' || managedOwner;

  useEffect(() => {
    fetchAllData();
  }, [managedOwner]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let dealershipsEndpoint = isOwner 
        ? `${API_URL}/api/owners/dealerships`
        : `${API_URL}/api/dealerships`;
      
      if (managedOwner && isOwner) {
        dealershipsEndpoint += `?ownerId=${managedOwner.id}`;
      }
        
      const dealershipsRes = await fetch(dealershipsEndpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (dealershipsRes.ok) {
        const dealershipsData = await dealershipsRes.json();
        const fetchedDealerships = dealershipsData.dealerships || [];
        setDealerships(fetchedDealerships);
        
        // Fetch motorcycle, apparel, and parts inventory for all dealerships
        const allInventory = { motorcycle: [], apparel: [], parts: [] };
        
        const entriesPromises = fetchedDealerships.map(async (d) => {
          const endpoints = {
            motorcycle: `${API_URL}/api/dealerships/${d.id}/inventory`,
            apparel: `${API_URL}/api/dealerships/${d.id}/apparel-inventory`,
            parts: `${API_URL}/api/dealerships/${d.id}/parts-inventory`
          };
          
          const inventoryData = { motorcycle: [], apparel: [], parts: [] };
          
          for (const [type, endpoint] of Object.entries(endpoints)) {
            try {
              const res = await fetch(endpoint, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (res.ok) {
                const data = await res.json();
                inventoryData[type] = (data.entries || []).map(e => ({
                  ...e,
                  dealershipId: d.id,
                  dealershipName: d.name,
                  type: type
                }));
              }
            } catch (err) {
              console.error(`Error fetching ${type} inventory:`, err);
            }
          }
          
          return inventoryData;
        });
        
        const inventoryArrays = await Promise.all(entriesPromises);

        // Merge all entries
        const combined = [];
        inventoryArrays.forEach(inv => {
          combined.push(...inv.motorcycle, ...inv.apparel, ...inv.parts);
        });

        setAllEntries(combined);

        // Fetch Open RO entries for all dealerships
        const openROPromises = fetchedDealerships.map(async (d) => {
          try {
            const res = await fetch(
              `${API_URL}/api/dealerships/${d.id}/weekly-service-metrics`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (res.ok) {
              const data = await res.json();
              return (data || []).map(e => ({
                ...e,
                dealershipId: d.id,
                dealershipName: d.name
              }));
            }
          } catch (err) {
            console.error(`Error fetching Open RO data for ${d.name}:`, err);
          }
          return [];
        });

        const openROArrays = await Promise.all(openROPromises);
        setOpenROEntries(openROArrays.flat());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewEntry = () => {
    const defaultDealership = dealerships.length > 0 ? dealerships[0].id : '';
    setEditingEntry({
      dealershipId: defaultDealership,
      date: getLocalDateString(),
      // Motorcycle
      motorcycleTotal: 0,
      motorcycleAnnualTurns: 0,
      motorcycleMonthlyTurns: 0,
      // Apparel
      apparelOnHandInventory: 0,
      apparelSpecialOrderLayaway: 0,
      apparelAnnualTurns: 0,
      apparelMonthlyTurns: 0,
      // Parts
      partsOnHandInventory: 0,
      partsSpecialOrderLayaway: 0,
      partsOnServiceRO: 0,
      partsAnnualTurns: 0,
      partsMonthlyTurns: 0,
      // Service Open ROs
      openROsInternal: 0,
      openROsCustomer: 0,
      notes: ''
    });
    setShowForm(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleEditOpenRO = (entry) => {
    setEditingEntry({
      id: entry.id,
      dealershipId: entry.dealershipId,
      date: toLocalDateString(entry.date),
      openROsInternal: entry.openROsInternal,
      openROsCustomer: entry.openROsCustomer,
      openROsPartsSales: entry.openROsPartsSales,
      openROsLaborSales: entry.openROsLaborSales,
      isOpenRO: true // Flag to indicate this is an Open RO edit
    });
    setShowForm(true);
  };

  const handleDeleteEntry = async (id, type) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      let deleteEndpoint;
      
      switch (type) {
        case 'apparel':
          deleteEndpoint = `${API_URL}/api/apparel-inventory/${id}`;
          break;
        case 'parts':
          deleteEndpoint = `${API_URL}/api/parts-inventory/${id}`;
          break;
        case 'motorcycle':
        default:
          deleteEndpoint = `${API_URL}/api/inventory/${id}`;
      }
      
      const response = await fetch(deleteEndpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete entry');
      }

      setAllEntries(prev => prev.filter(entry => entry.id !== id));
    } catch (error) {
      console.error('Delete error:', error);
      setAlertModal({ isOpen: true, title: 'Error', message: 'Failed to delete entry' });
    }
  };

  const handleDeleteOpenRO = async (id) => {
    if (!confirm('Are you sure you want to delete this Open RO entry?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/weekly-service-metrics/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete Open RO entry');
      }

      setOpenROEntries(prev => prev.filter(entry => entry.id !== id));
    } catch (error) {
      console.error('Delete error:', error);
      setAlertModal({ isOpen: true, title: 'Error', message: 'Failed to delete Open RO entry' });
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  const toggleAllDealerships = () => {
    if (selectedDealerships.length === dealerships.length) {
      setSelectedDealerships([]);
    } else {
      setSelectedDealerships(dealerships.map(d => d.id));
    }
  };

  const removeFilter = (type, value) => {
    if (type === 'dealership') {
      setSelectedDealerships(selectedDealerships.filter(d => d !== value));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredEntries = allEntries.filter(entry => {
    if (selectedDealerships.length > 0 && !selectedDealerships.includes(entry.dealershipId)) {
      return false;
    }
    if (searchTerm && !entry.dealershipName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (sortField === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (sortField === 'inventoryTotal' || sortField === 'annualTurns') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    }

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const activeFilters = [];
  selectedDealerships.forEach(id => {
    const dealership = dealerships.find(d => d.id === id);
    if (dealership) {
      activeFilters.push({ type: 'dealership', value: id, label: dealership.name });
    }
  });

  const downloadCSVTemplate = () => {
    const headers = ['Date Received', 'Dealership ID', 'Make', 'Model', 'Year', 'VIN', 'Cost', 'Status', 'Notes'];
    const sampleRow = ['2025-12-14', 'DEALERSHIP_ID_HERE', 'Harley-Davidson', 'Street 500', '2024', 'WVWZZZ3CZ9E123456', '7500', 'in_stock', 'Optional notes'];
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'inventory_template.csv';
    link.click();
  };

  const parseCsvFile = async (file) => {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must contain headers and at least one data row');
    }

    const csvHeaders = lines[0].split(',').map(h => h.trim());
    const dataLines = lines.slice(1);
    const requiredHeaders = ['Date Received', 'Dealership ID', 'Make', 'Model', 'Year', 'VIN', 'Cost'];
    const missingHeaders = requiredHeaders.filter(h => !csvHeaders.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
    }

    const rows = dataLines.map((line, index) => {
      const values = line.split(',').map(v => v.trim());
      const row = {};
      csvHeaders.forEach((header, i) => {
        row[header] = values[i] || '';
      });

      if (!row['Date Received'] || !row['Dealership ID'] || !row['Make'] || !row['Model'] || !row['Year'] || !row['VIN'] || !row['Cost']) {
        throw new Error(`Row ${index + 2}: Date Received, Dealership ID, Make, Model, Year, VIN, and Cost are required`);
      }

      return {
        dateReceived: row['Date Received'],
        dealershipId: row['Dealership ID'],
        make: row['Make'],
        model: row['Model'],
        year: row['Year'],
        vin: row['VIN'],
        cost: row['Cost'] || '0',
        status: row['Status'] || 'in_stock',
        notes: row['Notes'] || ''
      };
    });

    return rows;
  };

  const validateBulkEntries = async (entriesToValidate) => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}/api/vehicle-inventory/bulk-import/validate`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entries: entriesToValidate })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to validate import');
    }

    return response.json();
  };

  const executeBulkImport = async (entriesToImport) => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}/api/vehicle-inventory/bulk-import/execute`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entries: entriesToImport })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to import entries');
    }

    return response.json();
  };

  const handleBulkFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setBulkImportError(null);
      setBulkImportSuccess(null);
      setBulkFileName(file.name);

      const parsed = await parseCsvFile(file);
      setBulkEntries(parsed);

      const validation = await validateBulkEntries(parsed);
      setBulkValidation(validation);
      setBulkStep('review');
    } catch (err) {
      setBulkImportError(err.message);
      setBulkValidation(null);
    }

    event.target.value = '';
  };

  const handleBulkExecute = async () => {
    try {
      setBulkImportError(null);
      setBulkImportSuccess(null);
      setBulkStep('importing');

      const entriesToImport = bulkValidation?.normalizedEntries?.length ? bulkValidation.normalizedEntries : bulkEntries;
      const result = await executeBulkImport(entriesToImport);

      setBulkImportSuccess(`Imported ${result.imported} entries`);
      setBulkStep('done');

      await fetchAllData();
    } catch (err) {
      setBulkImportError(err.message);
      setBulkStep('review');
    }
  };

  if (loading) {
    return <LoadingState message="Loading inventory data..." fullscreen />;
  }

  // Inline form like DailyLogger (appears above table)

  return (
    <div className="sales-activity">
      <div className="sales-activity-header">
        <div>
          <h1>Weekly Inventory</h1>
          <p>Update motorcycle, apparel, and parts inventory for the week</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-bar-left">
          <button 
            className={`filter-button ${activeFilters.length > 0 ? 'has-filters' : ''}`}
            onClick={() => setShowFilterPanel(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round"/>
            </svg>
            Filters
            {activeFilters.length > 0 && ` (${activeFilters.length})`}
          </button>

          {activeFilters.length > 0 && (
            <div className="active-filters">
              {activeFilters.map((filter, index) => (
                <div key={index} className="active-filter-chip">
                  {filter.label}
                  <button onClick={() => removeFilter(filter.type, filter.value)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <>
          <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
          <div className="filter-panel open">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
            </div>

            <div className="filter-panel-content">
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Dealerships</h4>
                  <button onClick={toggleAllDealerships} className="btn-select-all">
                    {selectedDealerships.length === 0 ? 'Select All' : 'Clear All'}
                  </button>
                </div>
                {dealerships.map(dealership => (
                  <label key={dealership.id} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDealerships.includes(dealership.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDealerships([...selectedDealerships, dealership.id]);
                        } else {
                          setSelectedDealerships(selectedDealerships.filter(d => d !== dealership.id));
                        }
                      }}
                    />
                    <span>{dealership.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {showForm && (
        <div className="activities-form-container">
          <h2 className="form-heading">{editingEntry?.id ? 'Edit Weekly Inventory' : 'Weekly Inventory Entry'}</h2>
          <InventoryInputForm
            entry={editingEntry}
            dealerships={dealerships}
            onClose={handleCloseForm}
            onSave={() => {
              handleCloseForm();
              fetchAllData();
            }}
          />
        </div>
      )}

      <div className="activities-list">
        {/* View Mode Toggle Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
          <button
            onClick={() => setViewMode('inventory')}
            style={{
              padding: '12px 24px',
              background: viewMode === 'inventory' ? 'rgba(255, 122, 0, 0.2)' : 'transparent',
              border: 'none',
              borderBottom: viewMode === 'inventory' ? '2px solid #ff7a00' : '2px solid transparent',
              color: viewMode === 'inventory' ? '#ff7a00' : 'rgba(255, 255, 255, 0.6)',
              cursor: 'pointer',
              fontWeight: viewMode === 'inventory' ? '600' : '400',
              fontSize: '14px'
            }}
          >
            Inventory (Moto/Apparel/Parts)
          </button>
          <button
            onClick={() => setViewMode('openro')}
            style={{
              padding: '12px 24px',
              background: viewMode === 'openro' ? 'rgba(255, 122, 0, 0.2)' : 'transparent',
              border: 'none',
              borderBottom: viewMode === 'openro' ? '2px solid #ff7a00' : '2px solid transparent',
              color: viewMode === 'openro' ? '#ff7a00' : 'rgba(255, 255, 255, 0.6)',
              cursor: 'pointer',
              fontWeight: viewMode === 'openro' ? '600' : '400',
              fontSize: '14px'
            }}
          >
            Service Open ROs
          </button>
        </div>

        <div className="activities-controls">
          <div className="search-controls">
            <input
              type="text"
              placeholder="Search dealership..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {!showForm && (
          <button className="btn-new-sale" onClick={handleNewEntry}>
            + New Entry
          </button>
          )}
        </div>

        {viewMode === 'inventory' ? (
          sortedEntries.length === 0 ? (
            <div className="no-activities">
              <p>No entries found</p>
              {allEntries.length > 0 && <p className="filter-hint">Try adjusting your filters</p>}
              <button onClick={handleNewEntry}>Add first entry</button>
            </div>
          ) : (
            <div className="activities-table-container">
              <table className="activities-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('dealershipName')} className="sortable">
                      Dealership {sortField === 'dealershipName' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('date')} className="sortable">
                      Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('type')} className="sortable">
                      Type {sortField === 'type' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('inventoryTotal')} className="sortable">
                      Total ($) {sortField === 'inventoryTotal' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('rollingAnnualTurns')} className="sortable">
                      Annual Turns {sortField === 'rollingAnnualTurns' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('currentMonthlyTurns')} className="sortable">
                      Monthly Turns {sortField === 'currentMonthlyTurns' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.dealershipName}</td>
                      <td>{toLocalDateString(entry.date)}</td>
                      <td className="capitalize">{entry.type}</td>
                      <td>${(Math.round(entry.inventoryTotal * 100) / 100).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                      <td>{parseFloat(entry.rollingAnnualTurns || 0).toFixed(2)}</td>
                      <td>{parseFloat(entry.currentMonthlyTurns || 0).toFixed(2)}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleEditEntry(entry)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteEntry(entry.id, entry.type)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )
        ) : (
          // Open RO Table View
          openROEntries.filter(entry =>
            (selectedDealerships.length === 0 || selectedDealerships.includes(entry.dealershipId)) &&
            (searchTerm === '' || entry.dealershipName.toLowerCase().includes(searchTerm.toLowerCase()))
          ).length === 0 ? (
            <div className="no-activities">
              <p>No Open RO entries found</p>
              <button onClick={handleNewEntry}>Add first entry</button>
            </div>
          ) : (
            <div className="activities-table-container">
              <table className="activities-table">
                <thead>
                  <tr>
                    <th>Dealership</th>
                    <th>Date</th>
                    <th>Open ROs - Internal</th>
                    <th>Open ROs - Customer</th>
                    <th>Parts & Accessories Sales</th>
                    <th>Labor Sales</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {openROEntries
                    .filter(entry =>
                      (selectedDealerships.length === 0 || selectedDealerships.includes(entry.dealershipId)) &&
                      (searchTerm === '' || entry.dealershipName.toLowerCase().includes(searchTerm.toLowerCase()))
                    )
                    .sort((a, b) => {
                      const aDate = new Date(a.date);
                      const bDate = new Date(b.date);
                      return sortDirection === 'desc' ? bDate - aDate : aDate - bDate;
                    })
                    .map((entry) => (
                      <tr key={entry.id}>
                        <td>{entry.dealershipName}</td>
                        <td>{toLocalDateString(entry.date)}</td>
                        <td>{entry.openROsInternal}</td>
                        <td>{entry.openROsCustomer}</td>
                        <td>{formatCurrencyDisplay(entry.openROsPartsSales || 0)}</td>
                        <td>{formatCurrencyDisplay(entry.openROsLaborSales || 0)}</td>
                        <td>
                          <div className="table-actions">
                            <button className="btn-edit" onClick={() => handleEditOpenRO(entry)}>
                              Edit
                            </button>
                            <button className="btn-delete" onClick={() => handleDeleteOpenRO(entry.id)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )
        )}
      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        confirmStyle="primary"
        hideCancel={true}
        confirmText="OK"
      />
      </div>
    </div>
  );
}

function InventoryInputForm({ entry, dealerships, onClose, onSave }) {
  // Map database fields to form fields based on entry type
  const initializeFormData = () => {
    const base = {
      date: entry?.date ? toLocalDateString(entry.date) : getLocalDateString(),
      dealershipId: entry?.dealershipId || '',
      // Motorcycle
      motorcycleTotal: 0,
      motorcycleAnnualTurns: 0,
      motorcycleMonthlyTurns: 0,
      // Apparel
      apparelOnHandInventory: 0,
      apparelSpecialOrderLayaway: 0,
      apparelAnnualTurns: 0,
      apparelMonthlyTurns: 0,
      // Parts
      partsOnHandInventory: 0,
      partsSpecialOrderLayaway: 0,
      partsOnServiceRO: 0,
      partsAnnualTurns: 0,
      partsMonthlyTurns: 0,
      // Service Open ROs
      openROsInternal: entry?.openROsInternal || 0,
      openROsCustomer: entry?.openROsCustomer || 0,
      openROsPartsSales: entry?.openROsPartsSales || 0,
      openROsLaborSales: entry?.openROsLaborSales || 0,
      notes: entry?.notes || ''
    };

    // If editing, map the database fields to the form fields based on type
    if (entry?.id && entry?.type) {
      const typePrefix = entry.type;

      if (entry.type === 'parts') {
        base.partsOnHandInventory = entry?.onHandInventory || 0;
        base.partsSpecialOrderLayaway = entry?.specialOrderLayaway || 0;
        base.partsOnServiceRO = entry?.partsOnServiceRO || 0;
        base.partsAnnualTurns = entry?.rollingAnnualTurns || 0;
        base.partsMonthlyTurns = entry?.currentMonthlyTurns || 0;
      } else if (entry.type === 'apparel') {
        base.apparelOnHandInventory = entry?.onHandInventory || 0;
        base.apparelSpecialOrderLayaway = entry?.specialOrderLayaway || 0;
        base.apparelAnnualTurns = entry?.rollingAnnualTurns || 0;
        base.apparelMonthlyTurns = entry?.currentMonthlyTurns || 0;
      } else {
        // Motorcycle still uses Total
        base[`${typePrefix}Total`] = entry?.inventoryTotal || 0;
        base[`${typePrefix}AnnualTurns`] = entry?.rollingAnnualTurns || 0;
        base[`${typePrefix}MonthlyTurns`] = entry?.currentMonthlyTurns || 0;
      }
    }

    return base;
  };

  const [formData, setFormData] = useState(initializeFormData());
  const [displayValues, setDisplayValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const token = localStorage.getItem('token');

  // Reinitialize form when entry changes (when switching between editing different entries)
  useEffect(() => {
    setFormData(initializeFormData());
    setMessage({ type: '', text: '' });
  }, [entry?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // List of currency fields that need formatting (excluding Open RO sales fields which are now numeric inputs)
    const currencyFields = [
      'motorcycleTotal',
      'partsOnHandInventory', 'partsSpecialOrderLayaway', 'partsOnServiceRO',
      'apparelOnHandInventory', 'apparelSpecialOrderLayaway'
    ];

    if (currencyFields.includes(name)) {
      // Remove non-numeric characters except decimal point
      const numValue = value.replace(/[^0-9.]/g, '');
      setFormData(prev => ({ ...prev, [name]: numValue }));
      setDisplayValues(prev => ({ ...prev, [name]: formatCurrencyDisplay(numValue) }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Check if sections have data (at least one non-zero field)
      const hasMoto = parseFloat(formData.motorcycleTotal) || parseFloat(formData.motorcycleAnnualTurns) || parseFloat(formData.motorcycleMonthlyTurns);
      const hasApparel = parseFloat(formData.apparelOnHandInventory) || parseFloat(formData.apparelSpecialOrderLayaway) || parseFloat(formData.apparelAnnualTurns) || parseFloat(formData.apparelMonthlyTurns);
      const hasParts = parseFloat(formData.partsOnHandInventory) || parseFloat(formData.partsSpecialOrderLayaway) || parseFloat(formData.partsOnServiceRO) || parseFloat(formData.partsAnnualTurns) || parseFloat(formData.partsMonthlyTurns);
      const hasOpenROs = parseInt(formData.openROsInternal) || parseInt(formData.openROsCustomer) || parseFloat(formData.openROsPartsSales) || parseFloat(formData.openROsLaborSales);

      if (!hasMoto && !hasApparel && !hasParts && !hasOpenROs) {
        setMessage({ type: 'error', text: 'Please enter data for at least one category' });
        setLoading(false);
        return;
      }

      // Build submissions array with only non-blank sections
      const submissions = [];

      if (hasMoto) {
        submissions.push(
          fetch(
            entry?.id && entry?.type === 'motorcycle'
              ? `${API_URL}/api/inventory/${entry.id}`
              : `${API_URL}/api/dealerships/${formData.dealershipId}/inventory`,
            {
              method: entry?.id && entry?.type === 'motorcycle' ? 'PUT' : 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                date: formData.date,
                dealershipId: formData.dealershipId,
                inventoryTotal: parseFloat(formData.motorcycleTotal) || 0,
                rollingAnnualTurns: parseFloat(formData.motorcycleAnnualTurns) || 0,
                currentMonthlyTurns: parseFloat(formData.motorcycleMonthlyTurns) || 0,
                notes: formData.notes
              })
            }
          )
        );
      }

      if (hasApparel) {
        submissions.push(
          fetch(
            entry?.id && entry?.type === 'apparel'
              ? `${API_URL}/api/apparel-inventory/${entry.id}`
              : `${API_URL}/api/dealerships/${formData.dealershipId}/apparel-inventory`,
            {
              method: entry?.id && entry?.type === 'apparel' ? 'PUT' : 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                date: formData.date,
                dealershipId: formData.dealershipId,
                onHandInventory: parseFloat(formData.apparelOnHandInventory) || 0,
                specialOrderLayaway: parseFloat(formData.apparelSpecialOrderLayaway) || 0,
                rollingAnnualTurns: parseFloat(formData.apparelAnnualTurns) || 0,
                currentMonthlyTurns: parseFloat(formData.apparelMonthlyTurns) || 0,
                notes: formData.notes
              })
            }
          )
        );
      }

      if (hasParts) {
        submissions.push(
          fetch(
            entry?.id && entry?.type === 'parts'
              ? `${API_URL}/api/parts-inventory/${entry.id}`
              : `${API_URL}/api/dealerships/${formData.dealershipId}/parts-inventory`,
            {
              method: entry?.id && entry?.type === 'parts' ? 'PUT' : 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                date: formData.date,
                dealershipId: formData.dealershipId,
                onHandInventory: parseFloat(formData.partsOnHandInventory) || 0,
                specialOrderLayaway: parseFloat(formData.partsSpecialOrderLayaway) || 0,
                partsOnServiceRO: parseFloat(formData.partsOnServiceRO) || 0,
                rollingAnnualTurns: parseFloat(formData.partsAnnualTurns) || 0,
                currentMonthlyTurns: parseFloat(formData.partsMonthlyTurns) || 0,
                notes: formData.notes
              })
            }
          )
        );
      }

      const responses = await Promise.all(submissions);
      const allOk = responses.every(r => r.ok);

      if (!allOk) {
        const failedIndex = responses.findIndex(r => !r.ok);
        const types = hasMoto ? 'Motorcycle' : hasApparel ? 'Apparel' : 'Parts';
        throw new Error(`Failed to save ${types} inventory`);
      }

      // Save Service Open ROs if provided
      if (hasOpenROs) {
        console.log('Saving Open ROs:', {
          dealershipId: formData.dealershipId,
          date: formData.date,
          openROsInternal: parseInt(formData.openROsInternal) || 0,
          openROsCustomer: parseInt(formData.openROsCustomer) || 0
        });

        // Determine if this is an update or create
        const isOpenROEdit = entry?.isOpenRO && entry?.id;
        const url = isOpenROEdit
          ? `${API_URL}/api/weekly-service-metrics/${entry.id}`
          : `${API_URL}/api/dealerships/${formData.dealershipId}/weekly-service-metrics`;
        const method = isOpenROEdit ? 'PUT' : 'POST';

        const openROResponse = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            date: formData.date,
            openROsInternal: parseInt(formData.openROsInternal) || 0,
            openROsCustomer: parseInt(formData.openROsCustomer) || 0,
            openROsPartsSales: parseFloat(formData.openROsPartsSales) || 0,
            openROsLaborSales: parseFloat(formData.openROsLaborSales) || 0
          })
        });

        console.log('Open RO Response status:', openROResponse.status);

        if (!openROResponse.ok) {
          const errorData = await openROResponse.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Failed to save Open ROs:', errorData);
          setMessage({ type: 'error', text: `Failed to save Open ROs: ${errorData.error || 'Unknown error'}` });
          setLoading(false);
          return;
        }

        const openROData = await openROResponse.json();
        console.log('Open RO saved successfully:', openROData);
      }

      setMessage({ type: 'success', text: 'Weekly inventory saved successfully!' });
      setTimeout(() => {
        onSave();
      }, 500);
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save inventory' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="inventory-form">
      <div className="form-row">
        <div className="form-group">
          <label>Dealership *</label>
          <select
            name="dealershipId"
            value={formData.dealershipId}
            onChange={handleChange}
            required
            disabled={entry?.id}
          >
            <option value="">Select dealership</option>
            {dealerships.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Date *</label>
          <DatePicker
            asString
            hideLabel
            className="dp-form"
            selectedDate={formData.date}
            onDateChange={(val) => handleChange({ target: { name: 'date', value: val } })}
          />
        </div>
      </div>

      {/* Motorcycle Section */}
      <div className="inventory-section">
        <h3>Motorcycle Inventory</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Total ($)</label>
            <input
              type="text"
              name="motorcycleTotal"
              value={displayValues.motorcycleTotal || formatCurrencyDisplay(formData.motorcycleTotal)}
              onChange={handleChange}
              placeholder="$0"
            />
          </div>
          <div className="form-group">
            <label>Annual Turns</label>
            <input
              type="number"
              name="motorcycleAnnualTurns"
              value={formData.motorcycleAnnualTurns}
              onChange={handleChange}
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label>Monthly Turns</label>
            <input
              type="number"
              name="motorcycleMonthlyTurns"
              value={formData.motorcycleMonthlyTurns}
              onChange={handleChange}
              step="0.01"
            />
          </div>
        </div>
      </div>

      {/* Apparel Section */}
      <div className="inventory-section">
        <h3>Apparel & Licensing</h3>
        <div className="form-row">
          <div className="form-group">
            <label>On-Hand Inventory ($)</label>
            <input
              type="text"
              name="apparelOnHandInventory"
              value={displayValues.apparelOnHandInventory || formatCurrencyDisplay(formData.apparelOnHandInventory)}
              onChange={handleChange}
              placeholder="$0"
            />
          </div>
          <div className="form-group">
            <label>Special Order & Layaway ($)</label>
            <input
              type="text"
              name="apparelSpecialOrderLayaway"
              value={displayValues.apparelSpecialOrderLayaway || formatCurrencyDisplay(formData.apparelSpecialOrderLayaway)}
              onChange={handleChange}
              placeholder="$0"
            />
          </div>
          <div className="form-group">
            <label>Total ($) - Calculated</label>
            <input
              type="text"
              value={formatCurrencyDisplay((parseFloat(formData.apparelOnHandInventory) || 0) + (parseFloat(formData.apparelSpecialOrderLayaway) || 0))}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Annual Turns</label>
            <input
              type="number"
              name="apparelAnnualTurns"
              value={formData.apparelAnnualTurns}
              onChange={handleChange}
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label>Monthly Turns</label>
            <input
              type="number"
              name="apparelMonthlyTurns"
              value={formData.apparelMonthlyTurns}
              onChange={handleChange}
              step="0.01"
            />
          </div>
        </div>
      </div>

      {/* Parts Section */}
      <div className="inventory-section">
        <h3>Parts Inventory</h3>
        <div className="form-row">
          <div className="form-group">
            <label>On-Hand Inventory ($)</label>
            <input
              type="text"
              name="partsOnHandInventory"
              value={displayValues.partsOnHandInventory || formatCurrencyDisplay(formData.partsOnHandInventory)}
              onChange={handleChange}
              placeholder="$0"
            />
          </div>
          <div className="form-group">
            <label>Special Order & Layaway ($)</label>
            <input
              type="text"
              name="partsSpecialOrderLayaway"
              value={displayValues.partsSpecialOrderLayaway || formatCurrencyDisplay(formData.partsSpecialOrderLayaway)}
              onChange={handleChange}
              placeholder="$0"
            />
          </div>
          <div className="form-group">
            <label>Parts on Service Repair Orders ($)</label>
            <input
              type="text"
              name="partsOnServiceRO"
              value={displayValues.partsOnServiceRO || formatCurrencyDisplay(formData.partsOnServiceRO)}
              onChange={handleChange}
              placeholder="$0"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Total ($) - Calculated</label>
            <input
              type="text"
              value={formatCurrencyDisplay((parseFloat(formData.partsOnHandInventory) || 0) + (parseFloat(formData.partsSpecialOrderLayaway) || 0) + (parseFloat(formData.partsOnServiceRO) || 0))}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
          <div className="form-group">
            <label>Annual Turns</label>
            <input
              type="number"
              name="partsAnnualTurns"
              value={formData.partsAnnualTurns}
              onChange={handleChange}
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label>Monthly Turns</label>
            <input
              type="number"
              name="partsMonthlyTurns"
              value={formData.partsMonthlyTurns}
              onChange={handleChange}
              step="0.01"
            />
          </div>
        </div>
      </div>

      {/* Service Open ROs Section */}
      <div className="inventory-section">
        <h3>Service Open Repair Orders</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Open ROs - Internal (Count)</label>
            <input
              type="number"
              name="openROsInternal"
              value={formData.openROsInternal}
              onChange={handleChange}
              placeholder="0"
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Open ROs - Customer (Count)</label>
            <input
              type="number"
              name="openROsCustomer"
              value={formData.openROsCustomer}
              onChange={handleChange}
              placeholder="0"
              min="0"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Parts & Accessories Sales on Open ROs ($)</label>
            <input
              type="number"
              name="openROsPartsSales"
              value={formData.openROsPartsSales}
              onChange={handleChange}
              placeholder="0"
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Labor Sales on Open ROs ($)</label>
            <input
              type="number"
              name="openROsLaborSales"
              value={formData.openROsLaborSales}
              onChange={handleChange}
              placeholder="0"
              step="0.01"
              min="0"
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="form-row">
        <div className="form-group full-width">
          <label>Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Optional notes about this week's inventory..."
            rows={3}
          />
        </div>
      </div>

      {message.text && (
        <div className={`form-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="form-actions">
        <button type="button" onClick={onClose} className="btn-cancel">
          Cancel
        </button>
        <button type="submit" disabled={loading} className="btn-submit">
          {loading ? 'Saving...' : entry?.id ? 'Update Inventory' : 'Save Weekly Inventory'}
        </button>
      </div>
    </form>
  );
}

export default InventoryInput;
